import { makeAutoObservable } from "mobx";

import { TeamApi } from "@/models/api/TeamApi";
import { InvitesUX } from "@/store/invite/InvitesUX";
import { MembersUX } from "@/store/member/MembersUX";

import type { Team, TeamShort, UpdateTeamBody, UploadTeamAvatarBody } from "@/api";
import type { RootStore } from "@/store";

export class TeamModel {
  private rootStore: RootStore;

  private api: TeamApi;

  invitesUX: InvitesUX;

  membersUX: MembersUX;

  constructor(rootStore: RootStore, teamData: Team | TeamShort) {
    this.rootStore = rootStore;
    this.api = new TeamApi(teamData);
    this.invitesUX = new InvitesUX(this.rootStore, this);
    this.membersUX = new MembersUX(this.rootStore, this);

    makeAutoObservable(this);
  }

  subscribe() {
    this.api.subscribe();
  }

  unsubscribe() {
    this.api.unsubscribe();
  }

  get title() {
    return this.api.teamData.title || "";
  }

  get permalink() {
    return this.api.teamData.permalink;
  }

  get uid() {
    return this.api.teamData.uid;
  }

  get personal() {
    return this.api.teamData.personal;
  }

  get kind() {
    return this.api.teamData.kind;
  }

  get userPermissions() {
    return this.api.teamData.userPermissions;
  }

  get avatarUrls() {
    return this.api.teamData.avatarUrls;
  }

  get membersCount() {
    return this.api.teamData.membersCount;
  }

  get bio() {
    if ("bio" in this.api.teamData) {
      return this.api.teamData.bio;
    }
    return null;
  }

  get followersCount() {
    if ("followersCount" in this.api.teamData) {
      return this.api.teamData.followersCount;
    }
    return null;
  }

  get following() {
    if ("following" in this.api.teamData) {
      return this.api.teamData.following;
    }
    return false;
  }

  get unreadMessagesCount() {
    if ("unreadMessagesCount" in this.api.teamData) {
      return this.api.teamData.unreadMessagesCount;
    }
    return 0;
  }

  get root() {
    if ("root" in this.api.teamData) {
      return this.api.teamData.root;
    }
    return undefined;
  }

  get drafts() {
    if ("drafts" in this.api.teamData) {
      return this.api.teamData.drafts;
    }
    return undefined;
  }

  get trash() {
    if ("trash" in this.api.teamData) {
      return this.api.teamData.trash;
    }
    return undefined;
  }

  get published() {
    if ("published" in this.api.teamData) {
      return this.api.teamData.published;
    }
    return undefined;
  }

  async update(body: UpdateTeamBody) {
    const { team } = await this.api.update(body);
    return { team };
  }

  async uploadAvatar(body: UploadTeamAvatarBody) {
    const { team } = await this.api.uploadAvatar(body);
    return { team };
  }

  async leave() {
    return this.api.leave();
  }
}
